import React, { useEffect, useState } from 'react';

import { Group, Text, useMantineTheme } from '@mantine/core';
import { IconCircleCheckFilled, IconCircleFilled, IconCircleOff } from '@tabler/icons-react';
import Menu from 'components/Menu';
import Table from 'components/Table';
import { useAuth } from 'context/auth-context';
import dayjs from 'dayjs';
import useGyms from 'hooks/gym/useGyms';
import { useNavigate } from 'react-router-dom';
import { CellProps } from 'react-table';
import { GymList as Gym } from 'types/generated';

const AdminGymTableContainer = ({ search }: { search: string }): JSX.Element => {
  const theme = useMantineTheme();
  const { impersonate } = useAuth();

  const navigate = useNavigate();

  const [tableData, setTableData] = useState<Gym[]>([]);

  const { data, fetchNextPage, hasNextPage, isError, isSuccess, isLoading, isFetchingNextPage } = useGyms({
    search,
  });

  const getLastLoggedInCell = (lastLoggedIn: string | null): JSX.Element => {
    switch (!!lastLoggedIn) {
      case true:
        return (
          <Text size='sm' color='dimmed'>
            {dayjs(lastLoggedIn).format('MMM D, h:mm A')}
          </Text>
        );

      default:
        return (
          <Text size='sm' color='dimmed'>
            Never Logged In
          </Text>
        );
    }
  };

  const columns = [
    {
      Header: 'Name',
      accessor: 'title',
    },
    {
      Header: 'Gym Id',
      accessor: 'gymId',
    },
    {
      Header: 'Owner Name',
      Cell: ({ row }: CellProps<Gym>) => (
        <Text truncate='end' maw={200}>
          {row.original.owner.firstName} {row.original.owner.lastName}
        </Text>
      ),
    },
    {
      Header: 'Owner Email',
      Cell: ({ row }: CellProps<Gym>) => (
        <Text truncate='end' maw={200}>
          {row.original.owner.email}
        </Text>
      ),
    },
    {
      Header: 'Stripe Status',
      accessor: 'stripeStatus',
      Cell: ({ row }: CellProps<Gym>) => <Text>{row.original.stripeStatus}</Text>,
    },
    {
      Header: 'Last Login',
      Cell: ({ row }: CellProps<Gym>) => (
        <Group spacing={8}>
          <IconCircleFilled
            size={15}
            style={{
              color: row.original.owner.lastLogin ? theme.colors.green[5] : 'gray',
            }}
          />

          {getLastLoggedInCell(row.original.owner.lastLogin)}
        </Group>
      ),
    },
    {
      Header: 'GHL V2 Enabled',
      accessor: 'ghlV2Enabled',
      Cell: ({ row }: CellProps<Gym>) => (
        <Text>
          {row.original.ghlV2Enabled ? (
            <IconCircleCheckFilled size={theme.fontSizes.xl} style={{ color: theme.colors.kiloBlue[6] }} />
          ) : (
            <IconCircleOff size={theme.fontSizes.sm} />
          )}
        </Text>
      ),
    },
    {
      id: 'actions',
      Cell: ({ row }: CellProps<Gym>) => (
        <Group position='right'>
          <Menu
            items={[
              {
                title: 'Impersonate Owner',
                onClick: () => impersonate(row.original.owner.profile),
              },
              {
                title: 'View Staff',
                onClick: () => navigate(`/gyms/${row.original.gymId}/staff`),
              },
              {
                title: 'View Athletes',
                onClick: () => navigate(`/gyms/${row.original.gymId}/athletes`),
              },
            ]}
          />
        </Group>
      ),
      sticky: true,
    },
  ];

  useEffect(() => {
    if (!isLoading && data) {
      setTableData(data.pages.map(page => page.data.results).flat());
    }
  }, [isLoading, data, setTableData]);

  return (
    <>
      <Table
        tableData={tableData}
        columns={columns}
        isSuccess={isSuccess}
        isLoading={isLoading}
        isError={isError}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={!!hasNextPage}
        fetchNextPage={fetchNextPage}
      />
    </>
  );
};

export default AdminGymTableContainer;
